import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, LineChart } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";

const NAV = [
  { key: "about", hash: "#about" },
  { key: "how", hash: "#how" },
  { key: "advantages", hash: "#advantages" },
  { key: "faq", hash: "#faq" },
];

export default function Header() {
  const { t, lang, switchLanguage, buildPath } = useI18n();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const homePath = buildPath("");

  const isHome = location.pathname === homePath || location.pathname === `/${lang}/`;

  const anchorHref = (hash) => (isHome ? hash : `${homePath}${hash}`);

  const handleNavClick = () => setOpen(false);

  return (
    <header className="sticky top-0 z-40 w-full border-b border-slate-200 bg-white">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
        <Link
          to={homePath}
          data-testid="header-logo"
          className="flex items-center gap-2 text-slate-900"
          onClick={handleNavClick}
        >
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-teal-600 text-white">
            <LineChart className="h-5 w-5" strokeWidth={2.25} />
          </span>
          <span className="font-jakarta text-lg font-bold tracking-tight">{t("brand.name")}</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {NAV.map((item) => (
            <a
              key={item.key}
              href={anchorHref(item.hash)}
              data-testid={`header-nav-${item.key}`}
              className="text-sm font-medium text-slate-700 hover:text-teal-700 transition-colors"
            >
              {t(`header.nav.${item.key}`)}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <div className="inline-flex items-center rounded-full border border-slate-200 p-0.5 bg-white">
            <button
              type="button"
              data-testid="language-switcher-es"
              onClick={() => switchLanguage("ec")}
              className={`px-3 h-7 text-xs font-semibold rounded-full transition-colors ${
                lang === "ec" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
              aria-pressed={lang === "ec"}
            >
              {t("language.es_short")}
            </button>
            <button
              type="button"
              data-testid="language-switcher-en"
              onClick={() => switchLanguage("en")}
              className={`px-3 h-7 text-xs font-semibold rounded-full transition-colors ${
                lang === "en" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
              aria-pressed={lang === "en"}
            >
              {t("language.en_short")}
            </button>
          </div>
          <a href={anchorHref("#newsletter")} data-testid="header-cta">
            <Button className="bg-teal-600 hover:bg-teal-700 text-white h-9 rounded-md px-4 text-sm font-semibold">
              {t("header.cta")}
            </Button>
          </a>
        </div>

        <button
          type="button"
          className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-700 hover:bg-slate-100"
          data-testid="mobile-menu-toggle"
          aria-label={open ? t("header.menu_close") : t("header.menu_open")}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white" data-testid="mobile-menu">
          <nav className="px-4 py-4 space-y-1" aria-label="Mobile">
            {NAV.map((item) => (
              <a
                key={item.key}
                href={anchorHref(item.hash)}
                data-testid={`mobile-nav-${item.key}`}
                onClick={handleNavClick}
                className="block px-2 py-3 text-base font-medium text-slate-800 hover:bg-slate-50 rounded-md"
              >
                {t(`header.nav.${item.key}`)}
              </a>
            ))}
            <div className="pt-3 mt-3 border-t border-slate-200 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                {t("header.language")}
              </span>
              <div className="inline-flex items-center rounded-full border border-slate-200 p-0.5">
                <button
                  type="button"
                  data-testid="mobile-lang-es"
                  onClick={() => {
                    switchLanguage("ec");
                    setOpen(false);
                  }}
                  className={`px-3 h-7 text-xs font-semibold rounded-full ${
                    lang === "ec" ? "bg-slate-900 text-white" : "text-slate-600"
                  }`}
                >
                  {t("language.es_short")}
                </button>
                <button
                  type="button"
                  data-testid="mobile-lang-en"
                  onClick={() => {
                    switchLanguage("en");
                    setOpen(false);
                  }}
                  className={`px-3 h-7 text-xs font-semibold rounded-full ${
                    lang === "en" ? "bg-slate-900 text-white" : "text-slate-600"
                  }`}
                >
                  {t("language.en_short")}
                </button>
              </div>
            </div>
            <a
              href={anchorHref("#newsletter")}
              onClick={handleNavClick}
              data-testid="mobile-cta"
              className="mt-3 block"
            >
              <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white">
                {t("header.cta")}
              </Button>
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
